/**
 * paymentsReport — agregações puras do Relatório de Pagamentos.
 *
 * Fora do componente de propósito (o app já segue esse padrão com
 * `aggregateFinanceSummary`): tudo aqui é função pura sobre
 * `FinanceEntryWithStatus[]`, testável sem renderizar nada.
 *
 * Duas regras que este módulo existe para não deixar quebrar em silêncio:
 *
 * 1. **Datas são strings 'YYYY-MM-DD'** (colunas DATE, sem timezone). Toda
 *    comparação é lexicográfica sobre ISO zero-padded — nunca via `Date` +
 *    `toISOString()`, que em UTC-3 joga o dia para o anterior.
 * 2. **`payment_method` é nullable.** Um lançamento sem forma informada NÃO é
 *    descartado nem somado em "Outro" (que é um método real, `other`): vai para
 *    o balde explícito "Não informado".
 *
 * O status de vencimento vem do servidor (`computed_status` da VIEW
 * `finance_entries_with_status`); `computeFinanceStatus` só entra como espelho
 * local quando a linha veio do cache offline sem a coluna computada.
 */
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import { computeFinanceStatus, todayIsoDate } from '@/lib/financeStatus';
import type { FinanceEntryWithStatus, FinanceStatus, PaymentMethod } from '@/types/database';

/**
 * Qual coluna de data coloca um lançamento dentro do período:
 * - `paid`: data do pagamento — o **realizado**.
 * - `due`: data de vencimento — o **previsto**.
 * É sempre escolha explícita do usuário; misturar as duas em silêncio é o erro
 * clássico deste tipo de relatório.
 */
export type PaymentDateBasis = 'paid' | 'due';

export const DATE_BASIS_LABELS: Record<PaymentDateBasis, string> = {
  paid: 'Por data de pagamento',
  due: 'Por data de vencimento',
};

export interface IsoDateRange {
  from: string; // 'YYYY-MM-DD'
  to: string; // 'YYYY-MM-DD'
}

export const PAYMENTS_REPORT_SORT_FIELDS = [
  { value: 'date', label: 'Data', paidColumn: 'paid_date', dueColumn: 'due_date' },
  { value: 'amount', label: 'Valor', paidColumn: 'amount', dueColumn: 'amount' },
  { value: 'status', label: 'Status', paidColumn: 'computed_status', dueColumn: 'computed_status' },
  { value: 'description', label: 'Descrição', paidColumn: 'description', dueColumn: 'description' },
] as const;

export type PaymentsReportSortField = (typeof PAYMENTS_REPORT_SORT_FIELDS)[number]['value'];
export type PaymentsReportSortDirection = 'asc' | 'desc';

export interface PaymentsReportSort {
  field: PaymentsReportSortField;
  direction: PaymentsReportSortDirection;
}

export const DEFAULT_PAYMENTS_REPORT_SORT: PaymentsReportSort = {
  field: 'date',
  direction: 'desc',
};

export function paymentSortServerColumn(
  field: PaymentsReportSortField,
  basis: PaymentDateBasis,
): string {
  const option = PAYMENTS_REPORT_SORT_FIELDS.find((item) => item.value === field)
    ?? PAYMENTS_REPORT_SORT_FIELDS[0];
  return basis === 'paid' ? option.paidColumn : option.dueColumn;
}

/** Espelho estável da classificação do servidor para o cache offline. */
export function sortPaymentEntries(
  entries: readonly FinanceEntryWithStatus[],
  sort: PaymentsReportSort,
  basis: PaymentDateBasis,
): FinanceEntryWithStatus[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  const value = (entry: FinanceEntryWithStatus): string | number => {
    if (sort.field === 'date') return basisDate(entry, basis) ?? '';
    if (sort.field === 'amount') return Number(entry.amount ?? 0);
    if (sort.field === 'description') return entry.description ?? '';
    return effectiveStatus(entry);
  };
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((left, right) => {
      const a = value(left.entry);
      const b = value(right.entry);
      const aMissing = a == null || a === '';
      const bMissing = b == null || b === '';
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      const compared = typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' });
      return compared * direction || left.index - right.index;
    })
    .map(({ entry }) => entry);
}

/** Balde para `payment_method` nulo. Nunca confundir com `other` ("Outro"). */
export const UNSPECIFIED_METHOD_KEY = '__unspecified__';
export const UNSPECIFIED_METHOD_LABEL = 'Não informado';

/** Cor fixa por forma de pagamento (gráfico + legenda estáveis entre sessões). */
export const PAYMENT_METHOD_COLORS: Record<PaymentMethod | '__unspecified__', string> = {
  pix: '#10b981',
  cash: '#22c55e',
  card_credit: '#6366f1',
  card_debit: '#0ea5e9',
  boleto: '#f59e0b',
  transfer: '#8b5cf6',
  other: '#64748b',
  __unspecified__: '#94a3b8',
};

/**
 * Converte um `Date` (o `DateRangeFilter` trabalha com Date) para 'YYYY-MM-DD'
 * no **calendário local**. Reusa `todayIsoDate`, que lê getFullYear/Month/Date
 * em vez de `toISOString` — é o que evita o deslocamento de um dia em UTC-3.
 */
export function toIsoDateRange(from: Date, to: Date): IsoDateRange {
  return { from: todayIsoDate(from), to: todayIsoDate(to) };
}

/**
 * Status efetivo: o `computed_status` do servidor manda. `computeFinanceStatus`
 * é só o espelho local, para linhas vindas do cache offline sem a coluna.
 */
export function effectiveStatus(
  entry: FinanceEntryWithStatus,
  today: string = todayIsoDate(),
): FinanceStatus {
  return (entry.computed_status as FinanceStatus | null | undefined)
    ?? computeFinanceStatus(entry.status, entry.due_date, today);
}

/** Data que ancora o lançamento no período, conforme a base escolhida. */
export function basisDate(
  entry: FinanceEntryWithStatus,
  basis: PaymentDateBasis,
): string | null {
  const value = basis === 'paid' ? entry.paid_date : entry.due_date;
  return value ?? null;
}

function inIsoRange(date: string | null, range: IsoDateRange): boolean {
  if (!date) return false;
  if (range.from && date < range.from) return false;
  if (range.to && date > range.to) return false;
  return true;
}

/**
 * Lançamentos dentro do período pela base escolhida. Na base `paid`, quem não
 * tem `paid_date` simplesmente não tem data de pagamento e fica fora — não é
 * tratado como se tivesse sido pago hoje.
 */
export function scopeEntries(
  entries: FinanceEntryWithStatus[],
  range: IsoDateRange,
  basis: PaymentDateBasis,
): FinanceEntryWithStatus[] {
  return entries.filter((e) => inIsoRange(basisDate(e, basis), range));
}

/** Um lançamento efetivamente quitado (`status` gravado, não o computado). */
function isSettledPaid(entry: FinanceEntryWithStatus): boolean {
  return entry.status === 'paid';
}

function amountOf(entry: FinanceEntryWithStatus): number {
  const n = Number(entry.amount ?? 0);
  return Number.isFinite(n) ? n : 0;
}

// ===== KPIs =====

export interface PaymentsSummary {
  /** Entradas quitadas no período (base escolhida). */
  received: number;
  receivedCount: number;
  /** Saídas quitadas no período (base escolhida). */
  expensesPaid: number;
  expensesPaidCount: number;
  /** received − expensesPaid. */
  balance: number;
  /** Entradas em aberto e ainda no prazo — SEMPRE por vencimento. */
  toReceive: number;
  toReceiveCount: number;
  /** Entradas em aberto e atrasadas — SEMPRE por vencimento. */
  overdue: number;
  overdueCount: number;
}

/**
 * KPIs do período.
 *
 * `received`/`expensesPaid`/`balance` seguem a base escolhida (é o realizado
 * versus o previsto). Já `toReceive`/`overdue` são ancorados **sempre em
 * `due_date`**: um título em aberto não tem data de pagamento, então na base
 * `paid` eles simplesmente não existiriam. A tela rotula isso explicitamente.
 */
export function paymentsSummary(
  entries: FinanceEntryWithStatus[],
  range: IsoDateRange,
  basis: PaymentDateBasis,
  today: string = todayIsoDate(),
): PaymentsSummary {
  let received = 0;
  let receivedCount = 0;
  let expensesPaid = 0;
  let expensesPaidCount = 0;

  for (const e of scopeEntries(entries, range, basis)) {
    if (!isSettledPaid(e)) continue;
    if (e.kind === 'income') {
      received += amountOf(e);
      receivedCount += 1;
    } else {
      expensesPaid += amountOf(e);
      expensesPaidCount += 1;
    }
  }

  let toReceive = 0;
  let toReceiveCount = 0;
  let overdue = 0;
  let overdueCount = 0;

  for (const e of scopeEntries(entries, range, 'due')) {
    if (e.kind !== 'income') continue;
    const st = effectiveStatus(e, today);
    if (st === 'overdue') {
      overdue += amountOf(e);
      overdueCount += 1;
    } else if (st === 'pending') {
      toReceive += amountOf(e);
      toReceiveCount += 1;
    }
  }

  return {
    received,
    receivedCount,
    expensesPaid,
    expensesPaidCount,
    balance: received - expensesPaid,
    toReceive,
    toReceiveCount,
    overdue,
    overdueCount,
  };
}

// ===== Recebimentos por forma de pagamento =====

export interface PaymentMethodSlice {
  /** `null` quando a forma não foi informada. */
  method: PaymentMethod | null;
  key: string;
  label: string;
  total: number;
  count: number;
  /** 0–100. Zero quando não há recebimento algum (nunca NaN). */
  percent: number;
  color: string;
}

/**
 * Recebimentos (entradas quitadas) agrupados por forma de pagamento, do maior
 * para o menor. Lançamentos sem forma informada viram o balde
 * "Não informado" — não são descartados nem jogados em "Outro".
 */
export function aggregateByPaymentMethod(
  entries: FinanceEntryWithStatus[],
  range: IsoDateRange,
  basis: PaymentDateBasis,
): PaymentMethodSlice[] {
  const buckets = new Map<string, { method: PaymentMethod | null; total: number; count: number }>();

  for (const e of scopeEntries(entries, range, basis)) {
    if (e.kind !== 'income' || !isSettledPaid(e)) continue;
    const method = (e.payment_method as PaymentMethod | null) ?? null;
    const key = method ?? UNSPECIFIED_METHOD_KEY;
    const bucket = buckets.get(key) ?? { method, total: 0, count: 0 };
    bucket.total += amountOf(e);
    bucket.count += 1;
    buckets.set(key, bucket);
  }

  const grandTotal = Array.from(buckets.values()).reduce((sum, b) => sum + b.total, 0);

  return Array.from(buckets.entries())
    .map(([key, b]) => ({
      method: b.method,
      key,
      label: b.method ? PAYMENT_METHOD_LABELS[b.method] : UNSPECIFIED_METHOD_LABEL,
      total: b.total,
      count: b.count,
      // Guarda de divisão por zero: total 0 (ou só valores zerados) → 0%.
      percent: grandTotal > 0 ? (b.total / grandTotal) * 100 : 0,
      color: PAYMENT_METHOD_COLORS[(key as PaymentMethod | '__unspecified__')] ?? '#94a3b8',
    }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
}

// ===== Evolução mensal =====

const MONTH_ABBR = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

export interface MonthlyPaymentPoint {
  /** 'YYYY-MM'. */
  month: string;
  /** Rótulo curto para o eixo ('mar' ou 'mar/25' quando o período cruza anos). */
  label: string;
  received: number;
  paid: number;
}

/**
 * Lista de meses do período (inclusive os vazios, para o gráfico não "pular"
 * meses) com recebido × pago. Os meses são gerados por aritmética sobre a
 * string 'YYYY-MM', sem passar por `Date` — nada de deslocamento de fuso.
 */
export function aggregateMonthly(
  entries: FinanceEntryWithStatus[],
  range: IsoDateRange,
  basis: PaymentDateBasis,
): MonthlyPaymentPoint[] {
  const months = monthsBetween(range.from.slice(0, 7), range.to.slice(0, 7));
  if (months.length === 0) return [];

  const totals = new Map<string, { received: number; paid: number }>();
  for (const month of months) totals.set(month, { received: 0, paid: 0 });

  for (const e of scopeEntries(entries, range, basis)) {
    if (!isSettledPaid(e)) continue;
    const month = (basisDate(e, basis) ?? '').slice(0, 7);
    const bucket = totals.get(month);
    if (!bucket) continue; // fora da grade (não deveria ocorrer após o scope)
    if (e.kind === 'income') bucket.received += amountOf(e);
    else bucket.paid += amountOf(e);
  }

  const multiYear = months[0].slice(0, 4) !== months[months.length - 1].slice(0, 4);

  return months.map((month) => {
    const [year, m] = month.split('-');
    const abbr = MONTH_ABBR[Number(m) - 1] ?? month;
    const bucket = totals.get(month) ?? { received: 0, paid: 0 };
    return {
      month,
      label: multiYear ? `${abbr}/${year.slice(2)}` : abbr,
      received: bucket.received,
      paid: bucket.paid,
    };
  });
}

/** Meses 'YYYY-MM' de `from` até `to`, inclusive. */
function monthsBetween(from: string, to: string): string[] {
  if (!/^\d{4}-\d{2}$/.test(from) || !/^\d{4}-\d{2}$/.test(to) || from > to) return [];
  const out: string[] = [];
  let year = Number(from.slice(0, 4));
  let month = Number(from.slice(5, 7));
  // Guarda de sanidade: períodos absurdos não geram uma lista infinita.
  for (let i = 0; i < 600; i++) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    out.push(key);
    if (key >= to) break;
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return out;
}

// ===== Top clientes =====

export interface TopClientReceived {
  clientId: string | null;
  name: string;
  total: number;
  count: number;
}

export interface FinanceCategorySlice {
  categoryId: string | null;
  name: string;
  income: number;
  expense: number;
  balance: number;
  count: number;
}

/**
 * Receitas e despesas por categoria no mesmo recorte/base do relatório.
 * Na base realizada entram somente os quitados; cancelados nunca entram.
 */
export function aggregateByFinanceCategory(
  entries: readonly FinanceEntryWithStatus[],
  categoryNames: ReadonlyMap<string, string>,
  range: IsoDateRange,
  basis: PaymentDateBasis,
  sort: Pick<PaymentsReportSort, 'field' | 'direction'> = DEFAULT_PAYMENTS_REPORT_SORT,
): FinanceCategorySlice[] {
  const buckets = new Map<string, FinanceCategorySlice>();

  for (const entry of scopeEntries([...entries], range, basis)) {
    if (entry.status === 'canceled') continue;
    if (basis === 'paid' && entry.status !== 'paid') continue;
    const key = entry.category_id ?? '__none__';
    const bucket = buckets.get(key) ?? {
      categoryId: entry.category_id ?? null,
      name: entry.category_id
        ? categoryNames.get(entry.category_id) ?? 'Categoria removida'
        : 'Sem categoria',
      income: 0,
      expense: 0,
      balance: 0,
      count: 0,
    };
    const amount = amountOf(entry);
    if (entry.kind === 'income') bucket.income += amount;
    else bucket.expense += amount;
    bucket.balance = bucket.income - bucket.expense;
    bucket.count += 1;
    buckets.set(key, bucket);
  }

  const direction = sort.direction === 'asc' ? 1 : -1;
  return [...buckets.values()].sort((a, b) => {
    if (sort.field === 'description') {
      return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }) * direction;
    }
    if (sort.field === 'amount') {
      return ((a.income + a.expense) - (b.income + b.expense)) * direction
        || a.name.localeCompare(b.name, 'pt-BR');
    }
    return ((a.income + a.expense) - (b.income + b.expense)) * direction
      || a.name.localeCompare(b.name, 'pt-BR');
  });
}

/**
 * Top clientes por valor recebido. `clientNames` mapeia id → nome (o cadastro,
 * lido do cache offline); sem correspondência cai em "Cliente removido", e
 * lançamento sem `client_id` vira "Sem cliente".
 */
export function topClientsByReceived(
  entries: FinanceEntryWithStatus[],
  clientNames: Map<string, string>,
  range: IsoDateRange,
  basis: PaymentDateBasis,
  limit = 5,
): TopClientReceived[] {
  const buckets = new Map<string, { clientId: string | null; total: number; count: number }>();

  for (const e of scopeEntries(entries, range, basis)) {
    if (e.kind !== 'income' || !isSettledPaid(e)) continue;
    const key = e.client_id ?? '__none__';
    const bucket = buckets.get(key) ?? { clientId: e.client_id ?? null, total: 0, count: 0 };
    bucket.total += amountOf(e);
    bucket.count += 1;
    buckets.set(key, bucket);
  }

  return Array.from(buckets.values())
    .map((b) => ({
      clientId: b.clientId,
      name: b.clientId
        ? clientNames.get(b.clientId) ?? 'Cliente removido'
        : 'Sem cliente',
      total: b.total,
      count: b.count,
    }))
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
    .slice(0, limit);
}

// ===== Vencidos =====

export interface OverdueRow {
  id: string;
  description: string;
  amount: number;
  dueDate: string;
  daysLate: number;
  clientName: string | null;
}

/**
 * Entradas em atraso no período, da mais antiga para a mais nova — o item mais
 * acionável do relatório. O "vencido" vem de `computed_status` (servidor); só
 * cai no espelho local quando a linha veio do cache sem a coluna computada.
 * Sempre ancorado em `due_date`, independente da base escolhida (um título em
 * aberto não tem data de pagamento).
 */
export function overdueEntries(
  entries: FinanceEntryWithStatus[],
  range: IsoDateRange,
  clientNames: Map<string, string> = new Map(),
  today: string = todayIsoDate(),
): OverdueRow[] {
  return scopeEntries(entries, range, 'due')
    .filter((e) => e.kind === 'income')
    .filter((e) => effectiveStatus(e, today) === 'overdue')
    .map((e) => ({
      id: e.id,
      description: e.description,
      amount: amountOf(e),
      dueDate: e.due_date,
      daysLate: daysBetweenIso(e.due_date, today),
      clientName: e.client_id ? clientNames.get(e.client_id) ?? null : null,
    }))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || b.amount - a.amount);
}

/**
 * Dias inteiros de `from` até `to` ('YYYY-MM-DD'). Ancorado em meia-noite UTC
 * para os dois lados, então horário de verão/fuso não muda a contagem.
 */
export function daysBetweenIso(from: string, to: string): number {
  const a = isoToUtcMs(from);
  const b = isoToUtcMs(to);
  if (a === null || b === null) return 0;
  return Math.round((b - a) / 86_400_000);
}

function isoToUtcMs(value: string | null | undefined): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  return Date.UTC(year, month - 1, day);
}
